import React, { useMemo, useState } from "react"
import { AutoSendSettingCard } from "./AutoSendSettingCard"
import { DraftDecisionLog } from "./DraftDecisionLog"
import { type PromptPreviewParams, useDunningPromptPreview } from "../hooks/useDunningPromptPreview"
import {
  PROMPT_NOTE_MAX_LENGTH,
  useDunningSystemPromptVersions,
} from "../hooks/useDunningSystemPromptVersions"
import type { DunningSystemPromptVersion } from "../types"
import {
  AutoEvaluateOption,
  AutoEvaluateResult,
  type AutoEvaluateState,
  useAutoEvaluateOnSave,
} from "../evaluation/batch/AutoEvaluateOnSave"
import type { Locate } from "../evaluation/navigation"
import { splitVersions } from "../evaluation/components/PromptPicker"
import { formatDateTime } from "./shared"

/**
 * AIプロンプト設定ページ（/admin/dunning-prompt-settings）: AIドラフトのシステムプロンプト・バージョン管理。
 * 上書き更新APIは存在しない（仕様）ため、「編集」は有効版の内容をプリフィルした
 * 新バージョン作成として表現する。「このバージョンに戻す」= activate（ロールバック）。
 */

const MODEL_OPTIONS = ["gemini-3.7-flash", "gemini-3.6-flash", "gemini-2.5-flash", "gemini-2.5-pro"]
const MAX_PROMPT_CONTENT_BYTES = 1024 * 1024

const utf8ByteLength = (value: string) => {
  let bytes = 0

  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0
    bytes += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4
  }

  return bytes
}
const formatByteCount = (value: number) => new Intl.NumberFormat("ja-JP").format(value)

const ActiveBadge: React.FC = () => (
  <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-[1.1rem] font-semibold text-green-700">
    有効
  </span>
)

/** プレビュー用ケースID入力の共有props（前回入力したIDをページ内で共有する） */
type PreviewCaseIdProps = {
  previewCaseId: string
  onPreviewCaseIdChange: (value: string) => void
}

/**
 * 指定した督促ケースに対するプロンプトのプレビューUI。
 * 使うとどのようなドラフトメッセージになるかを試し生成して表示する。
 * エディタ（編集中の content/model）とバージョン履歴の行（promptVersionId）の両方から使う。
 * dry-run のため、実行しても何も保存されない。
 */
const PromptPreviewSection: React.FC<
  PreviewCaseIdProps & {
    /** 実行時のリクエストパラメータを組み立てる（編集中の最新値を拾えるよう関数で受ける） */
    buildParams: (dunningCaseId: number) => PromptPreviewParams
    buttonLabel: string
    /** プロンプト側の入力が不足している場合に実行を止める（例: エディタで本文が空） */
    paramsIncompleteReason?: string
  }
> = ({
  previewCaseId,
  onPreviewCaseIdChange,
  buildParams,
  buttonLabel,
  paramsIncompleteReason,
}) => {
  const { result, isPreviewing, previewError, runPreview } = useDunningPromptPreview()

  const parsedCaseId = /^\d+$/.test(previewCaseId.trim())
    ? Number.parseInt(previewCaseId.trim(), 10)
    : null
  const canRun =
    parsedCaseId != null && parsedCaseId > 0 && !paramsIncompleteReason && !isPreviewing

  const handleRun = () => {
    if (parsedCaseId == null) return
    void runPreview(buildParams(parsedCaseId))
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[1.25rem] font-semibold text-black">プレビュー実行</span>
        <input
          type="number"
          min={1}
          inputMode="numeric"
          className="w-[140px] rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[1.25rem] tabular-nums text-black focus:border-primary focus:outline-none"
          placeholder="ケースID"
          value={previewCaseId}
          onChange={(event) => onPreviewCaseIdChange(event.target.value)}
        />
        <button
          type="button"
          disabled={!canRun}
          onClick={handleRun}
          className="rounded-lg border border-primary bg-white px-4 py-1.5 text-[1.25rem] font-medium text-primary hover:bg-primary/5 disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-white"
        >
          {isPreviewing ? "生成中…" : buttonLabel}
        </button>
        {result && !isPreviewing && (
          <button
            type="button"
            disabled={!canRun}
            onClick={handleRun}
            className="text-[1.2rem] text-black underline hover:text-black disabled:opacity-50"
          >
            再実行
          </button>
        )}
      </div>
      <p className="mt-1.5 text-[1.15rem] text-black">
        ケースIDは督促ケース一覧のIDです。プレビューは試し生成のみで、ドラフトや履歴は一切保存されません。
      </p>
      {paramsIncompleteReason && (
        <p className="mt-1 text-[1.15rem] text-amber-600">{paramsIncompleteReason}</p>
      )}

      {isPreviewing && (
        <p className="mt-3 text-[1.25rem] text-black">
          ケース #{previewCaseId} の状況をもとにドラフトを生成しています…
        </p>
      )}

      {previewError && !isPreviewing && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[1.25rem] text-red-700">
          プレビューに失敗しました: {previewError}
        </div>
      )}

      {result && !isPreviewing && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[1.2rem] font-semibold text-black">生成されたドラフト</span>
            <span className="rounded-full bg-gray-200 px-2.5 py-0.5 text-[1.1rem] font-medium text-black">
              使用モデル: {result.model}
            </span>
            <span className="rounded-full bg-cyan-50 px-2.5 py-0.5 text-[1.1rem] font-medium text-cyan-700">
              {result.generationMetadata.mode}
            </span>
            <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-[1.1rem] font-medium text-violet-700">
              {result.generationMetadata.intent}
            </span>
            {result.generationMetadata.humanReviewRequired && (
              <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[1.1rem] font-semibold text-red-700">
                自動送信不可・要確認
              </span>
            )}
            {result.generationMetadata.humanFollowRecommended && (
              <span
                className="cursor-help rounded-full bg-sky-50 px-2.5 py-0.5 text-[1.1rem] font-semibold text-sky-700"
                title={`自動送信しません。${result.generationMetadata.humanFollowReason ?? ""}`}
              >
                人の対応推奨
              </span>
            )}
            {result.generationMetadata.callFollowUp && (
              <span
                className="cursor-help rounded-full bg-sky-50 px-2.5 py-0.5 text-[1.1rem] font-semibold text-sky-700"
                title="自動送信しません。電話でお伝えした内容と合っているか確認してから送ってください。"
              >
                電話後の案内
              </span>
            )}
          </div>
          {/* チャット吹き出し風の文面表示 */}
          <div className="max-w-[640px] whitespace-pre-wrap rounded-2xl rounded-tl-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-[1.3rem] leading-relaxed text-black">
            {result.body}
          </div>
          <DraftDecisionLog
            decision={result.generationMetadata.decision}
            observation={result.generationMetadata.decisionObservation}
            defaultOpen
          />
          <details className="rounded-lg border border-gray-200 bg-white">
            <summary className="cursor-pointer px-4 py-2.5 text-[1.2rem] font-medium text-black hover:text-black">
              モデルに送られたケース情報を見る
            </summary>
            <pre className="max-h-[320px] overflow-auto whitespace-pre-wrap border-t border-gray-200 px-4 py-3 font-mono text-[1.15rem] leading-relaxed text-black">
              {result.userPrompt}
            </pre>
          </details>
        </div>
      )}
    </div>
  )
}

/** 新バージョン作成フォーム（新規登録・編集の両方で使用） */
const PromptEditor: React.FC<
  PreviewCaseIdProps & {
    initialContent: string
    initialModel: string
    isSubmitting: boolean
    onSave: (params: {
      content: string
      model: string
      activate: boolean
      note: string
    }) => Promise<boolean>
    onCancel?: () => void
    /** 保存したら回帰テストで比較を実行するかの切り替え */
    autoEvaluate?: AutoEvaluateState
  }
> = ({
  initialContent,
  initialModel,
  isSubmitting,
  onSave,
  onCancel,
  previewCaseId,
  onPreviewCaseIdChange,
  autoEvaluate,
}) => {
  const [content, setContent] = useState(initialContent)
  const [model, setModel] = useState(initialModel)
  const [note, setNote] = useState("")
  const isKnownModel = MODEL_OPTIONS.includes(model)
  const [useCustomModel, setUseCustomModel] = useState(!isKnownModel && model !== "")
  // 本文全体を走査するため、model 等の他 state 更新で再計算しないよう content にだけ依存させる
  const contentBytes = useMemo(() => utf8ByteLength(content), [content])
  const contentTooLarge = contentBytes > MAX_PROMPT_CONTENT_BYTES

  const canSubmit =
    content.trim() !== "" && model.trim() !== "" && !contentTooLarge && !isSubmitting

  const handleSave = async (activate: boolean) => {
    const ok = await onSave({ content: content.trim(), model: model.trim(), activate, note })
    if (ok && onCancel) onCancel()
  }

  return (
    <div className="space-y-4">
      <label className="flex flex-col gap-1">
        <span className="text-[1.15rem] font-medium text-black">モデル</span>
        <div className="flex flex-wrap items-center gap-3">
          <select
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-[1.3rem] text-black focus:border-primary focus:outline-none"
            value={useCustomModel ? "__custom__" : model}
            onChange={(event) => {
              if (event.target.value === "__custom__") {
                setUseCustomModel(true)
              } else {
                setUseCustomModel(false)
                setModel(event.target.value)
              }
            }}
          >
            {MODEL_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
            <option value="__custom__">その他（自由入力）</option>
          </select>
          {useCustomModel && (
            <input
              type="text"
              className="w-[280px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-[1.3rem] text-black focus:border-primary focus:outline-none"
              placeholder="モデル名を入力"
              value={model}
              onChange={(event) => setModel(event.target.value)}
            />
          )}
        </div>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[1.15rem] font-medium text-black">システムプロンプト本文</span>
        <textarea
          rows={14}
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 font-mono text-[1.25rem] leading-relaxed text-black focus:border-primary focus:outline-none"
          placeholder="AIドラフト生成に使うシステムプロンプトを入力"
          value={content}
          onChange={(event) => setContent(event.target.value)}
        />
        <span
          className={`text-[1.15rem] tabular-nums ${
            contentTooLarge ? "font-medium text-red-600" : "text-black"
          }`}
        >
          UTF-8: {formatByteCount(contentBytes)} / {formatByteCount(MAX_PROMPT_CONTENT_BYTES)}{" "}
          バイト
        </span>
        {contentTooLarge && (
          <span className="text-[1.15rem] text-red-600">
            システムプロンプト本文が上限を超えています。短くしてから保存してください。
          </span>
        )}
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[1.15rem] font-medium text-black">メモ（任意）</span>
        <input
          type="text"
          maxLength={PROMPT_NOTE_MAX_LENGTH}
          className="w-full max-w-[640px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-[1.3rem] text-black focus:border-primary focus:outline-none"
          placeholder="例: 一部入金の言い回し調整"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </label>
      {/* 編集中の content / model をそのまま試せるプレビュー（保存不要） */}
      <PromptPreviewSection
        previewCaseId={previewCaseId}
        onPreviewCaseIdChange={onPreviewCaseIdChange}
        buttonLabel="このケースでプレビュー"
        buildParams={(dunningCaseId) => ({
          dunningCaseId,
          content: content.trim(),
          model: model.trim(),
        })}
        paramsIncompleteReason={
          contentTooLarge
            ? "プロンプト本文がUTF-8の上限を超えているためプレビューできません。"
            : content.trim() === "" || model.trim() === ""
              ? "プレビューにはプロンプト本文とモデルの入力が必要です。"
              : undefined
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => handleSave(true)}
          className="rounded-xl bg-primary px-5 py-2.5 text-[1.3rem] font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {isSubmitting ? "保存中…" : "保存して有効化"}
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => handleSave(false)}
          className="rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-[1.3rem] font-medium text-black hover:bg-gray-50 disabled:opacity-50"
        >
          下書きとして保存
        </button>
        {onCancel && (
          <button
            type="button"
            disabled={isSubmitting}
            onClick={onCancel}
            className="text-[1.25rem] text-black underline hover:text-black disabled:opacity-50"
          >
            キャンセル
          </button>
        )}
      </div>
      {autoEvaluate && <AutoEvaluateOption state={autoEvaluate} disabled={isSubmitting} />}
    </div>
  )
}

/** 履歴テーブルのメモの欄。「編集」でその場で書き換える（空で保存すると消える） */
const NoteCell: React.FC<{
  version: DunningSystemPromptVersion
  onSave: (versionId: number, note: string) => Promise<unknown>
}> = ({ version, onSave }) => {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const start = () => {
    setValue(version.note ?? "")
    setError(null)
    setEditing(true)
  }
  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await onSave(version.id, value)
      setEditing(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : "メモの保存に失敗しました。")
    } finally {
      setSaving(false)
    }
  }

  if (!editing)
    return (
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 text-[1.25rem] text-black [overflow-wrap:anywhere]">
          {version.note || "—"}
        </span>
        <button
          type="button"
          onClick={start}
          className="shrink-0 text-[1.15rem] text-black underline hover:opacity-70"
        >
          編集
        </button>
      </div>
    )
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          aria-label={`v${version.version} のメモ`}
          maxLength={PROMPT_NOTE_MAX_LENGTH}
          autoFocus
          className="w-[260px] max-w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-[1.25rem] text-black focus:border-primary focus:outline-none"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing) void save()
            if (event.key === "Escape") setEditing(false)
          }}
        />
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className="rounded-lg border border-primary bg-white px-3 py-1 text-[1.15rem] font-medium text-primary hover:bg-primary/5 disabled:opacity-50"
        >
          保存
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => setEditing(false)}
          className="text-[1.15rem] text-black underline disabled:opacity-50"
        >
          キャンセル
        </button>
      </div>
      {error && <span className="text-[1.15rem] text-red-600">{error}</span>}
    </div>
  )
}

/** 履歴テーブルの1行（行クリックで本文を展開表示） */
const VersionRow: React.FC<
  PreviewCaseIdProps & {
    version: DunningSystemPromptVersion
    isSubmitting: boolean
    onRollback: (version: DunningSystemPromptVersion) => void
    onSaveNote: (versionId: number, note: string) => Promise<unknown>
  }
> = ({ version, isSubmitting, onRollback, onSaveNote, previewCaseId, onPreviewCaseIdChange }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <tr
        className="cursor-pointer border-b border-gray-100 transition-colors odd:bg-white even:bg-gray-50/50 hover:bg-gray-50"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <td className="px-4 py-3 text-[1.3rem] font-bold tabular-nums text-black">
          v{version.version}
        </td>
        {/* メモの編集中に行を押しても本文を開閉しない */}
        <td className="min-w-[200px] px-4 py-3" onClick={(event) => event.stopPropagation()}>
          <NoteCell version={version} onSave={onSaveNote} />
        </td>
        <td className="px-4 py-3 text-[1.25rem] text-black">{version.model}</td>
        <td className="px-4 py-3">
          {version.active ? <ActiveBadge /> : <span className="text-[1.2rem] text-black">—</span>}
        </td>
        <td className="px-4 py-3 text-[1.25rem] tabular-nums text-black">
          {version.activatedAt ? formatDateTime(version.activatedAt) : "—"}
        </td>
        <td className="px-4 py-3 text-[1.25rem] tabular-nums text-black">
          {formatDateTime(version.createdAt)}
        </td>
        <td className="px-4 py-3">
          {version.active ? (
            <span className="text-[1.2rem] text-black">使用中</span>
          ) : (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={(event) => {
                event.stopPropagation()
                onRollback(version)
              }}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[1.2rem] font-medium text-black hover:bg-gray-50 disabled:opacity-50"
            >
              このバージョンに戻す
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-gray-100">
          <td colSpan={HISTORY_HEADERS.length} className="space-y-3 bg-gray-50 px-4 py-3">
            <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-white px-4 py-3 font-mono text-[1.2rem] leading-relaxed text-black">
              {version.content}
            </pre>
            {/* 保存済みバージョン（promptVersionId）でのプレビュー */}
            <PromptPreviewSection
              previewCaseId={previewCaseId}
              onPreviewCaseIdChange={onPreviewCaseIdChange}
              buttonLabel="このバージョンでプレビュー"
              buildParams={(dunningCaseId) => ({
                dunningCaseId,
                promptVersionId: version.id,
              })}
            />
          </td>
        </tr>
      )}
    </>
  )
}

const HISTORY_HEADERS = ["v", "メモ", "モデル", "状態", "有効化日時", "作成日時", "操作"]

export const SystemPromptSettingsView: React.FC<{
  /** プロンプトの比較のタブへ移る（保存時に始めたまとめて生成を開く） */
  onLocate?: Locate
}> = ({ onLocate }) => {
  const {
    versions,
    isLoading,
    error,
    createVersion,
    activateVersion,
    updateNote,
    isSubmitting,
    submitError,
    clearSubmitError,
    autoEvaluation,
    clearAutoEvaluation,
  } = useDunningSystemPromptVersions()
  const autoEvaluate = useAutoEvaluateOnSave()

  const [isEditing, setIsEditing] = useState(false)
  const [rollbackTarget, setRollbackTarget] = useState<DunningSystemPromptVersion | null>(null)
  // プレビュー用ケースID: ページ内（エディタ・履歴の各行）で共有し、前回入力を保持する
  const [previewCaseId, setPreviewCaseId] = useState("")

  const activeVersion = versions.find((v) => v.active)
  // 版の一覧は有効版と新しい版3本だけ出し、ほかは「ほかの版」で開く
  const { shown: shownVersions, rest: restVersions } = splitVersions(versions)
  const [showAllVersions, setShowAllVersions] = useState(false)

  const handleSave = async (params: {
    content: string
    model: string
    activate: boolean
    note: string
  }): Promise<boolean> => {
    const created = await createVersion({ ...params, autoEvaluateCaseSet: autoEvaluate.caseSet })
    return created != null
  }

  const handleRollback = async () => {
    if (!rollbackTarget) return
    const activated = await activateVersion(rollbackTarget.id)
    if (activated) setRollbackTarget(null)
  }

  if (isLoading) {
    return <p className="py-10 text-center text-[1.3rem] text-black">読み込み中…</p>
  }

  return (
    <div className="space-y-5">
      {error != null && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-[1.3rem] text-red-700">
          データの取得に失敗しました。API が利用可能か確認してください。
        </div>
      )}

      {submitError && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-[1.3rem] text-red-700">
          <span>{submitError}</span>
          <button
            type="button"
            onClick={clearSubmitError}
            className="shrink-0 text-[1.2rem] text-red-500 underline hover:text-red-700"
          >
            閉じる
          </button>
        </div>
      )}

      {autoEvaluation && (
        <AutoEvaluateResult
          result={autoEvaluation}
          onOpen={
            onLocate ? (batchId) => onLocate({ tab: "regression", batch: batchId }) : undefined
          }
          onClose={clearAutoEvaluation}
        />
      )}

      {/* 有効バージョンカード or 未登録案内 */}
      {activeVersion ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4">
            <h2 className="text-[1.5rem] font-bold text-black">有効バージョン</h2>
            <ActiveBadge />
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[1.15rem] font-semibold tabular-nums text-black">
              {activeVersion.promptKey} / v{activeVersion.version}
            </span>
            {activeVersion.note && (
              <span className="text-[1.2rem] text-black">{activeVersion.note}</span>
            )}
            <span className="text-[1.2rem] text-black">{activeVersion.model}</span>
            {activeVersion.activatedAt && (
              <span className="text-[1.2rem] tabular-nums text-black">
                有効化: {formatDateTime(activeVersion.activatedAt)}
              </span>
            )}
            {!isEditing && (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="ml-auto rounded-xl bg-primary px-4 py-2 text-[1.3rem] font-medium text-white hover:opacity-90"
              >
                編集して新バージョン作成
              </button>
            )}
          </div>
          <div className="px-5 py-4">
            {isEditing ? (
              <PromptEditor
                initialContent={activeVersion.content}
                initialModel={activeVersion.model}
                isSubmitting={isSubmitting}
                onSave={handleSave}
                onCancel={() => setIsEditing(false)}
                previewCaseId={previewCaseId}
                onPreviewCaseIdChange={setPreviewCaseId}
                autoEvaluate={autoEvaluate}
              />
            ) : (
              <pre className="max-h-[360px] overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 font-mono text-[1.25rem] leading-relaxed text-black">
                {activeVersion.content}
              </pre>
            )}
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm">
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-4">
            <h2 className="text-[1.5rem] font-bold text-amber-800">プロンプト未登録</h2>
            <p className="mt-1 text-[1.25rem] text-amber-700">
              有効なシステムプロンプトがありません。登録するまでAIドラフト生成は使えません（422エラーになります）。下のフォームから作成してください。
            </p>
          </div>
          <div className="px-5 py-4">
            <PromptEditor
              initialContent=""
              initialModel={MODEL_OPTIONS[0]}
              isSubmitting={isSubmitting}
              onSave={handleSave}
              previewCaseId={previewCaseId}
              onPreviewCaseIdChange={setPreviewCaseId}
              autoEvaluate={autoEvaluate}
            />
          </div>
        </div>
      )}

      {/* バージョン履歴テーブル */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
          <h2 className="text-[1.4rem] font-bold text-black">
            バージョン履歴
            <span className="ml-2 rounded-full bg-gray-200 px-2.5 py-0.5 text-[1.15rem] font-semibold tabular-nums text-black">
              {versions.length}
            </span>
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                {HISTORY_HEADERS.map((label) => (
                  <th
                    key={label}
                    className="whitespace-nowrap px-4 py-2.5 text-left text-[1.15rem] font-semibold text-black"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {versions.length === 0 && (
                <tr>
                  <td
                    colSpan={HISTORY_HEADERS.length}
                    className="px-4 py-6 text-center text-[1.3rem] text-black"
                  >
                    バージョンはまだありません。
                  </td>
                </tr>
              )}
              {[...shownVersions, ...(showAllVersions ? restVersions : [])].map((version) => (
                <VersionRow
                  key={version.id}
                  version={version}
                  isSubmitting={isSubmitting}
                  onRollback={setRollbackTarget}
                  onSaveNote={updateNote}
                  previewCaseId={previewCaseId}
                  onPreviewCaseIdChange={setPreviewCaseId}
                />
              ))}
              {!showAllVersions && restVersions.length > 0 && (
                <tr>
                  <td colSpan={HISTORY_HEADERS.length} className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={() => setShowAllVersions(true)}
                      className="text-[1.25rem] text-black underline hover:opacity-70"
                    >
                      ほかの版（{restVersions.length}）
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 自動送付設定 */}
      <AutoSendSettingCard />

      {/* ロールバック確認ダイアログ */}
      {rollbackTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-[480px] rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-[1.5rem] font-bold text-black">
              v{rollbackTarget.version} に戻しますか？
            </h3>
            <p className="mt-2 text-[1.3rem] leading-relaxed text-black">
              {rollbackTarget.promptKey} / v{rollbackTarget.version}（{rollbackTarget.model}
              ）を有効バージョンにします。以降のAIドラフト生成はこのプロンプトを使用します。
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setRollbackTarget(null)}
                className="rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-[1.3rem] font-medium text-black hover:bg-gray-50 disabled:opacity-50"
              >
                キャンセル
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleRollback}
                className="rounded-xl bg-primary px-5 py-2.5 text-[1.3rem] font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting ? "有効化中…" : "このバージョンに戻す"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
